import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import React from "react";
import { Eye, EyeOff } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface LeadInfoTypeProps {
  data: any;
  onDataChanged: (data: any) => void;
}

const leadFields = ["first_name", "last_name", "email", "phone"] as const;
type LeadField = (typeof leadFields)[number];

type LeadFieldConfig = {
  enabled: boolean;
  required: boolean;
};

const defaultFields: Record<LeadField, LeadFieldConfig> = {
  first_name: { enabled: true, required: false },
  last_name: { enabled: true, required: false },
  email: { enabled: true, required: false },
  phone: { enabled: true, required: false },
};

const LeadInfoType: React.FC<LeadInfoTypeProps> = ({ data, onDataChanged }) => {
  const fields = normalizeFields(data?.fields);

  const toggle = (field: LeadField) => (checked: boolean) => {
    onDataChanged({
      ...(data ?? {}),
      fields: {
        ...fields,
        [field]: {
          ...fields[field],
          enabled: checked,
          required: checked ? fields[field].required : false,
        },
      },
    });
  };
  const toggleRequired = (field: LeadField) => (checked: boolean) => {
    onDataChanged({
      ...(data ?? {}),
      fields: {
        ...fields,
        [field]: {
          enabled: fields[field].enabled || checked,
          required: checked,
        },
      },
    });
  };

  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
      <p className="font-medium text-foreground">Contact info fields</p>
      {leadFields.map((field) => (
        <div key={field} className="flex items-center justify-between gap-4">
          <span className={fields[field].enabled ? undefined : "opacity-40"}>
            {field
              .replace("_", " ")
              .replace(/\b\w/g, (char) => char.toUpperCase())}
          </span>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-pressed={fields[field].enabled}
                  onClick={() => toggle(field)(!fields[field].enabled)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                >
                  {fields[field].enabled ? (
                    <Eye className="h-4 w-4" />
                  ) : (
                    <EyeOff className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {fields[field].enabled ? "Hide field" : "Show field"}
              </TooltipContent>
            </Tooltip>
            <span
              className={
                fields[field].enabled
                  ? "text-sm font-medium text-muted-foreground"
                  : "text-sm font-medium text-muted-foreground opacity-40"
              }
            >
              Required
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Switch
                    size="sm"
                    className="dark:data-[state=unchecked]:bg-foreground/25"
                    aria-label={
                      fields[field].required
                        ? `Mark ${field.replace("_", " ")} as optional`
                        : `Mark ${field.replace("_", " ")} as required`
                    }
                    checked={fields[field].required}
                    disabled={!fields[field].enabled}
                    onCheckedChange={(checked) => {
                      if (!fields[field].enabled) {
                        return;
                      }
                      toggleRequired(field)(checked);
                    }}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {fields[field].enabled
                  ? fields[field].required
                    ? "Mark as optional"
                    : "Mark as required"
                  : "Hidden fields cannot be required"}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
  );
};

export default LeadInfoType;

function normalizeFields(
  input?: Record<string, boolean | LeadFieldConfig> | null,
): Record<LeadField, LeadFieldConfig> {
  if (!input) {
    return { ...defaultFields };
  }

  const normalized = { ...defaultFields };
  for (const field of leadFields) {
    const value = input[field];
    if (typeof value === "boolean") {
      normalized[field] = { enabled: value, required: false };
    } else if (value && typeof value === "object") {
      normalized[field] = {
        enabled: value.enabled ?? true,
        required: value.required ?? false,
      };
    }
  }

  return normalized;
}
